import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Wraps the learner LEXP viewer so a runtime render error surfaces as a
 * visible fallback card instead of the browser showing a blank white page.
 *
 * Previously any exception (e.g. an undefined component reference, an
 * `answer_options.map` on missing data) would tear down the whole route
 * and the learner would just see nothing. That was reported in production
 * as "the LEXP won't load".
 */
export default class LexpErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[LexpErrorBoundary] LEXP render crashed:", error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="max-w-2xl mx-auto my-10 rounded-xl border border-amber-200 bg-amber-50 p-6"
          data-testid="lexp-error-boundary"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h2 className="font-heading font-bold text-lg text-amber-900">
                This learning experience couldn't render fully
              </h2>
              <p className="text-sm text-amber-800 font-body mt-1">
                Part of the page had an unexpected error, but your progress is
                safe. Try reloading — if it keeps happening, let your instructor
                know.
              </p>
              <button
                type="button"
                onClick={this.handleReload}
                data-testid="lexp-error-reload-btn"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-body"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
